import { useConfirm } from "@/contexts/ConfirmDialogContext";
import { api } from "@/lib/api";
import { Badge, Button, Field, Input, Label, Text } from "@a7y/tailwind-catalyst";
import { useMutation } from "@tanstack/react-query";
import { useState, type ChangeEvent } from "react";
import { Section } from "../shared";
import type { Affiliate, AffiliateCouponCode } from "../types";

type StatusMessage = { type: "success" | "error"; text: string };

export function CouponDiscountTab({
  affiliate,
  coupon,
  onChange,
}: {
  affiliate: Affiliate;
  coupon: AffiliateCouponCode | null;
  onChange: () => void;
}) {
  const confirm = useConfirm();
  const [percent, setPercent] = useState<string>(
    affiliate.customerDiscountPercent != null ? String(affiliate.customerDiscountPercent) : "",
  );
  const [statusMessage, setStatusMessage] = useState<StatusMessage | null>(null);

  const showStatus = (type: "success" | "error", text: string) => {
    setStatusMessage({ type, text });
    setTimeout(() => setStatusMessage(null), 5000);
  };

  const setDiscountMutation = useMutation({
    mutationFn: (value: number | null) => api.affiliates.setDiscount(affiliate.id, value),
    onSuccess: () => {
      showStatus("success", "Discount saved — Stripe coupon provisioning triggered");
      onChange();
    },
    onError: (err: Error) => showStatus("error", `Save failed: ${err.message}`),
  });

  const parsed = percent.trim() === "" ? null : Number(percent);
  const invalid = parsed != null && (Number.isNaN(parsed) || parsed < 0 || parsed > 100);

  const handleSave = async () => {
    if (invalid) return;
    const ok = await confirm({
      title: parsed == null ? "Clear customer discount?" : `Set discount to ${parsed}%?`,
      description:
        parsed == null
          ? "Clearing deactivates and deletes the affiliate's Stripe coupon and promotion code."
          : "Saving provisions (or re-points) the affiliate's Stripe coupon and promotion code for this percentage.",
      confirmLabel: "Save",
      confirmColor: "amber",
    });
    if (ok) setDiscountMutation.mutate(parsed);
  };

  return (
    <div className="space-y-6">
      <Section title="Customer discount">
        <Text className="mb-4 text-sm text-zinc-500 dark:text-zinc-400">
          The customer-facing discount this affiliate's coupon grants at checkout. Setting a
          percentage provisions a Stripe coupon (duration: forever) and promotion code; clearing it
          tears them down.
        </Text>
        {statusMessage && (
          <div
            className={`mb-4 rounded-lg px-4 py-2.5 text-sm font-medium ${
              statusMessage.type === "success"
                ? "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400"
                : "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400"
            }`}
          >
            {statusMessage.text}
          </div>
        )}
        <div className="flex flex-wrap items-end gap-3">
          <Field className="w-40">
            <Label>Discount %</Label>
            <Input
              type="number"
              min={0}
              max={100}
              value={percent}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setPercent(e.target.value)}
              placeholder="e.g. 20"
            />
          </Field>
          <Button
            color="blue"
            disabled={invalid || setDiscountMutation.isPending}
            onClick={handleSave}
          >
            {setDiscountMutation.isPending ? "Saving..." : "Save discount"}
          </Button>
        </div>
        {invalid && (
          <Text className="mt-2 text-sm text-red-500">Enter a whole percentage between 0 and 100.</Text>
        )}
      </Section>

      <Section title="Stripe coupon">
        {coupon ? (
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
            <Detail label="Code" value={<span className="font-mono">{coupon.code}</span>} />
            <Detail
              label="Stripe coupon id"
              value={
                coupon.stripeCouponId ? (
                  <span className="font-mono text-xs">{coupon.stripeCouponId}</span>
                ) : (
                  <Badge color="amber">Not provisioned</Badge>
                )
              }
            />
            <Detail
              label="Stripe promotion code id"
              value={
                coupon.stripePromotionCodeId ? (
                  <span className="font-mono text-xs">{coupon.stripePromotionCodeId}</span>
                ) : (
                  <Badge color="amber">Not provisioned</Badge>
                )
              }
            />
          </dl>
        ) : (
          <Text className="text-sm text-zinc-500">
            No coupon code exists for this affiliate yet. Setting a discount above will create one.
          </Text>
        )}
      </Section>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 text-sm">
      <dt className="text-xs font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
        {label}
      </dt>
      <dd className="text-zinc-900 dark:text-zinc-100">{value}</dd>
    </div>
  );
}
